import { useState, useRef, useEffect } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { useTonConnectUI } from '@tonconnect/ui-react'
import { Copy, Check, Settings, Plus, Pencil } from 'lucide-react'
import type { AppContext } from '../components/layout/AppLayout'
import { BottomSheet } from '../components/ui'
import CreateKeySheet from '../components/CreateKeySheet'
import { chatStorage } from '../lib/chat-storage'
import { api, type UsageInfo } from '../lib/api'
import { haptics } from '../lib/haptics'
import { timeAgo } from '../lib/time-ago'

interface RecentActivity {
  modelName: string
  updatedAt: number
  tokens: number
}


export default function HomePage() {
  const { balance, refreshBalance, wallet } = useOutletContext<AppContext>()
  const [tonConnectUI] = useTonConnectUI()
  const navigate = useNavigate()
  const connectWallet = () => { haptics.impact('medium'); tonConnectUI.openModal() }
  const creditsUsd = balance?.creditsUsd ?? 0
  const credits = balance?.credits ? BigInt(balance.credits) : 0n

  const [createKeyOpen, setCreateKeyOpen] = useState(false)
  const [keyOptionsOpen, setKeyOptionsOpen] = useState(false)
  const [selectedKeyId, setSelectedKeyId] = useState<string | null>(null)
  const [copiedLabel, setCopiedLabel] = useState<string | null>(null)
  const [revokeConfirm, setRevokeConfirm] = useState(false)
  const [revokeError, setRevokeError] = useState<string | null>(null)
  const [nameDraft, setNameDraft] = useState('')
  const [nameEditing, setNameEditing] = useState(false)
  const nameInputRef = useRef<HTMLInputElement>(null)
  const nameOriginalRef = useRef('')  // name at the moment editing started — for dirty check
  const [usageData, setUsageData] = useState<UsageInfo | null>(null)

  const address = wallet?.account.address
  // Read fresh every render — so changes made during revoke are picked up immediately
  const storedKey = address ? (localStorage.getItem(`imago:key:${address}`) ?? '') : ''

  const keys = balance?.keys && balance.keys.length > 0
    ? balance.keys
    : storedKey
      ? [{ id: 'local', name: 'Default key', defaultModel: '', createdAt: '' }]
      : []

  const sessions = chatStorage.list()

  // ── Fetch real usage from API ─────────────────────────────────────────────
  useEffect(() => {
    if (!storedKey) {
      setUsageData(null)
      return
    }
    let cancelled = false
    api.getUsage()
      .then(data => { if (!cancelled) setUsageData(data) })
      .catch(() => { /* silently ignore — no usage to show */ })
    return () => { cancelled = true }
  }, [storedKey])

  /** Display formatter for dollar amounts */
  const fmtCost = (c: number): string => {
    if (c === 0) return '—'
    if (c < 0.00005) return '<$0.0001'
    if (c < 0.01) return `$${c.toFixed(4)}`
    return `$${c.toFixed(2)}`
  }

  const usageSorted = (usageData?.usage ?? []).slice(0, 4)
  const maxCost = (usageSorted[0]?.costUsd ?? 0) > 0 ? usageSorted[0].costUsd : 1
  const totalCost = usageData?.totalCostUsd ?? 0

  const recentActivity: RecentActivity[] = (!wallet ? [] : sessions)
    .filter(s => s.messages.some((m: any) => m.role === 'assistant'))
    .slice(0, 5)
    .map(s => {
      const assistantMsgs = s.messages.filter((m: any) => m.role === 'assistant')
      const last = assistantMsgs[assistantMsgs.length - 1] as any
      const tokens = last ? Math.ceil((last.content?.length || 0) / 4) : 0
      return { modelName: s.modelName, updatedAt: s.updatedAt, tokens }
    })

  const handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text)
      haptics.notification('success')
      setCopiedLabel(label)
      setTimeout(() => setCopiedLabel(null), 2000)
    } catch {
      haptics.notification('error')
    }
  }

  const handleRevoke = async () => {
    if (!selectedKeyId || selectedKeyId === 'local' || !address) return
    try {
      const currentStoredKey = localStorage.getItem(`imago:key:${address}`) ?? ''
      const perIdSecret = localStorage.getItem(`imago:key:${address}:${selectedKeyId}`) ?? ''
      const selectedIdx = keys.findIndex((k) => k.id === selectedKeyId)
      // Legacy keys (created before per-ID storage) only have the top-level key.
      // For the first (active) key slot, fall back to storedKey if per-ID is missing.
      const revokedSecret = !perIdSecret && selectedIdx === 0 ? currentStoredKey : perIdSecret
      const isActiveKey = !!revokedSecret && revokedSecret === currentStoredKey

      // If revoking the active auth key — switch to another key first so the
      // DELETE request is authenticated with a different key (server rejects
      // self-revocation with the same token)
      if (isActiveKey) {
        const otherKey = keys.find((k) => k.id !== selectedKeyId && k.id !== 'local')
        const otherSecret = otherKey
          ? (localStorage.getItem(`imago:key:${address}:${otherKey.id}`) ?? '')
          : ''
        if (otherSecret) {
          api.setKey(otherSecret)
          localStorage.setItem(`imago:key:${address}`, otherSecret)
        } else {
          // No usable other key — just clear auth and let server handle it
          api.setKey('')
          localStorage.removeItem(`imago:key:${address}`)
        }
      }

      await api.revokeKey(selectedKeyId, address)
      haptics.notification('success')

      // Clean up per-ID key secret from localStorage
      localStorage.removeItem(`imago:key:${address}:${selectedKeyId}`)

      // If we cleared auth above and have no replacement, stay signed out
      if (isActiveKey && !keys.find((k) => k.id !== selectedKeyId && k.id !== 'local')) {
        localStorage.removeItem(`imago:key:${address}`)
        api.setKey('')
      }

      setKeyOptionsOpen(false)
      refreshBalance()
    } catch (err: unknown) {
      haptics.notification('error')
      setRevokeError(err instanceof Error ? err.message : 'Failed to revoke key')
    }
  }

  /** Read local name override — falls back to API name */
  const getKeyDisplayName = (keyId: string, apiName: string): string =>
    (address ? localStorage.getItem(`imago:keyname:${address}:${keyId}`) ?? null : null) || apiName

  const handleSaveName = () => {
    const trimmed = nameDraft.trim()
    if (!trimmed || !selectedKeyId || !address) return
    localStorage.setItem(`imago:keyname:${address}:${selectedKeyId}`, trimmed)
    setNameEditing(false)
    haptics.notification('success')
  }

  const canRevoke = selectedKeyId !== 'local'
  // null = still loading (show normal color), 0 = loaded but empty (show muted)
  const balanceColor = balance === null || creditsUsd > 0
    ? 'var(--tg-theme-text-color)'
    : 'var(--tg-theme-hint-color)'

  return (
    <>
      <div className="flex-1 scroll-area min-h-0">

        {/* ── Balance Hero ── */}
        <div className="px-sp-5 pt-sp-8 pb-sp-6 flex flex-col items-center text-center">
          <p className="text-[13px] font-medium mb-sp-3" style={{ color: 'var(--tg-theme-hint-color)' }}>
            Balance
          </p>
          {balance === null && wallet ? (
            <div className="h-[56px] w-36 rounded-[10px] animate-pulse mb-sp-1" style={{ backgroundColor: 'var(--tg-theme-secondary-bg-color)' }} />
          ) : (
            <p className="text-[56px] font-bold tabular-nums leading-none" style={{ color: balanceColor }}>
              ${creditsUsd.toFixed(2)}
            </p>
          )}
          <p className="text-[14px] tabular-nums mt-2 mb-sp-3" style={{ color: 'var(--tg-theme-hint-color)' }}>
            {balance === null && wallet ? '—' : `${credits.toLocaleString()} credits`}
          </p>
          {(balance !== null || !wallet) && creditsUsd === 0 ? (
            <p className="text-[12px] mb-sp-3" style={{ color: 'var(--tg-theme-hint-color)' }}>
              Top up to start using AI models
            </p>
          ) : (
            <div className="mb-sp-3" />
          )}
          <div className="flex gap-sp-2 w-full">
            <button
              onClick={wallet ? () => { haptics.impact('medium'); navigate('/deposit') } : connectWallet}
              className="flex-1 h-14 rounded-[14px] text-[15px] font-semibold active:opacity-70 transition-opacity flex items-center justify-center gap-1.5"
              style={{ backgroundColor: 'var(--tg-theme-button-color)', color: 'var(--tg-theme-button-text-color)' }}
            >
              <span style={{ fontSize: '16px' }}>↑</span> Deposit
            </button>
          </div>
        </div>

        {/* ── API Keys ── */}
        <div className="px-sp-4 mb-sp-5">
          <p className="text-[11px] font-semibold uppercase tracking-wider mb-sp-2 px-sp-1" style={{ color: 'var(--tg-theme-hint-color)' }}>
            API Keys
          </p>
          <div className="rounded-[16px] overflow-hidden" style={{ backgroundColor: 'var(--tg-theme-secondary-bg-color)' }}>
            {!wallet ? (
              <div className="flex items-center justify-between px-sp-4 py-sp-4">
                <p className="text-[14px]" style={{ color: 'var(--tg-theme-hint-color)' }}>Connect wallet to create keys</p>
                <button
                  onClick={connectWallet}
                  className="text-[13px] font-semibold active:opacity-70 transition-opacity"
                  style={{ color: 'var(--tg-theme-accent-text-color)' }}
                >
                  Connect →
                </button>
              </div>
            ) : keys.length === 0 ? (
              <div className="flex items-center justify-between px-sp-4 py-sp-4">
                <p className="text-[14px]" style={{ color: 'var(--tg-theme-hint-color)' }}>No API keys</p>
                <button
                  onClick={() => { haptics.impact('light'); setCreateKeyOpen(true) }}
                  className="text-[13px] font-semibold active:opacity-70 transition-opacity"
                  style={{ color: 'var(--tg-theme-accent-text-color)' }}
                >
                  Create →
                </button>
              </div>
            ) : (
              keys.map((key, idx) => {
                const isActiveKey = idx === 0
                // Look up key secret: try per-ID storage first, then legacy storage for first key
                const keySecret = isActiveKey
                  ? (localStorage.getItem(`imago:key:${address}:${key.id}`) || storedKey || '')
                  : (localStorage.getItem(`imago:key:${address}:${key.id}`) || '')
                const canCopy = keySecret.length > 0
                const maskedSuffix = canCopy ? keySecret.slice(-4) : '????'

                return (
                  <div
                    key={key.id}
                    className="px-sp-4 py-sp-3"
                    style={idx < keys.length - 1 ? { borderBottom: '1px solid var(--tg-theme-bg-color)' } : undefined}
                  >
                    {/* Line 1: key name only */}
                    <div className="flex items-center mb-1 min-w-0">
                      <p className="text-[14px] font-medium truncate" style={{ color: 'var(--tg-theme-text-color)' }}>
                        {getKeyDisplayName(key.id, key.name)}
                      </p>
                    </div>
                    {/* Line 2: masked key + icons */}
                    <div className="flex items-center justify-between">
                      <p className="text-[13px] font-mono" style={{ color: 'var(--tg-theme-hint-color)' }}>
                        sk-imago-••••{maskedSuffix}
                      </p>
                      <div className="flex items-center gap-sp-3">
                        {canCopy && (
                          <button
                            onClick={() => handleCopy(keySecret, key.id)}
                            className="active:opacity-70 transition-opacity"
                            style={{ color: copiedLabel === key.id ? 'var(--tg-theme-button-color)' : 'var(--tg-theme-accent-text-color)' }}
                          >
                            {copiedLabel === key.id ? <Check size={16} /> : <Copy size={16} />}
                          </button>
                        )}
                        <button
                          onClick={() => {
                            haptics.impact('light')
                            setSelectedKeyId(key.id)
                            setRevokeConfirm(false)
                            setRevokeError(null)
                            const displayName = getKeyDisplayName(key.id, key.name)
                            setNameDraft(displayName)
                            nameOriginalRef.current = displayName
                            setNameEditing(false)
                            setKeyOptionsOpen(true)
                          }}
                          className="active:opacity-70 transition-opacity"
                          style={{ color: 'var(--tg-theme-hint-color)' }}
                        >
                          <Settings size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })
            )}

            {/* + New key — only shown when wallet connected and at least one key exists */}
            {wallet && keys.length > 0 && (
              <button
                onClick={() => { haptics.impact('light'); setCreateKeyOpen(true) }}
                className="w-full flex items-center gap-sp-2 px-sp-4 py-sp-3 active:opacity-70 transition-opacity"
                style={{ borderTop: '1px solid var(--tg-theme-bg-color)', color: 'var(--tg-theme-accent-text-color)' }}
              >
                <Plus size={15} />
                <span className="text-[14px] font-medium">New key</span>
              </button>
            )}
          </div>
        </div>

        {/* ── Usage ── */}
        <div className="px-sp-4 pb-sp-8">
          <div className="flex items-center justify-between mb-sp-2 px-sp-1">
            <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--tg-theme-hint-color)' }}>
              Usage
            </p>
            {totalCost > 0 && (
              <p className="text-[11px] tabular-nums" style={{ color: 'var(--tg-theme-hint-color)' }}>
                Total: {fmtCost(totalCost)}
              </p>
            )}
          </div>

          {recentActivity.length === 0 && usageSorted.length === 0 ? (
            <div
              className="rounded-[16px] flex flex-col items-center py-sp-6 gap-sp-2 text-center px-sp-4"
              style={{ backgroundColor: 'var(--tg-theme-secondary-bg-color)' }}
            >
              <p className="text-[14px]" style={{ color: 'var(--tg-theme-hint-color)' }}>No activity yet</p>
              {wallet ? (
                <button
                  onClick={() => { haptics.impact('light'); navigate('/chat') }}
                  className="text-[13px] font-medium active:opacity-70 transition-opacity"
                  style={{ color: 'var(--tg-theme-accent-text-color)' }}
                >
                  Start chatting →
                </button>
              ) : (
                <button
                  onClick={() => { haptics.impact('light'); navigate('/models') }}
                  className="text-[13px] font-medium active:opacity-70 transition-opacity"
                  style={{ color: 'var(--tg-theme-accent-text-color)' }}
                >
                  Browse models →
                </button>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-sp-3">
              {/* Per-model spending bars — real billing data from API */}
              {usageSorted.length > 0 && (
              <div className="rounded-[16px] overflow-hidden" style={{ backgroundColor: 'var(--tg-theme-secondary-bg-color)' }}>
                {usageSorted.map((u, idx) => (
                  <div
                    key={u.model}
                    className="px-sp-4 py-sp-3"
                    style={idx < usageSorted.length - 1 ? { borderBottom: '1px solid var(--tg-theme-bg-color)' } : undefined}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[13px] font-medium truncate flex-1 mr-2" style={{ color: 'var(--tg-theme-text-color)' }}>
                        {u.modelName}
                      </span>
                      <span className="text-[12px] tabular-nums shrink-0" style={{ color: 'var(--tg-theme-hint-color)' }}>
                        {fmtCost(u.costUsd)}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--tg-theme-bg-color)' }}>
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${Math.max(4, (u.costUsd / maxCost) * 100)}%`,
                          backgroundColor: 'var(--tg-theme-accent-text-color)',
                          opacity: 0.7,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              )}

              {/* Recent sessions */}
              {recentActivity.length > 0 && (
                <div className="rounded-[16px] overflow-hidden" style={{ backgroundColor: 'var(--tg-theme-secondary-bg-color)' }}>
                  {recentActivity.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between px-sp-4 py-sp-3"
                      style={idx < recentActivity.length - 1 ? { borderBottom: '1px solid var(--tg-theme-bg-color)' } : undefined}
                    >
                      <div className="min-w-0 flex-1 mr-3">
                        <p className="text-[13px] font-medium truncate" style={{ color: 'var(--tg-theme-text-color)' }}>
                          {item.modelName}
                        </p>
                        <p className="text-[11px] mt-0.5" style={{ color: 'var(--tg-theme-hint-color)' }}>
                          {timeAgo(item.updatedAt)} · ~{item.tokens > 1000 ? `${(item.tokens / 1000).toFixed(1)}K` : item.tokens} tokens
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Key Settings Sheet ── */}
      <BottomSheet
        open={keyOptionsOpen}
        onClose={() => {
          setKeyOptionsOpen(false)
          setRevokeConfirm(false)
          setRevokeError(null)
          setNameEditing(false)
        }}
        title="Key Settings"
      >
        <div className="pb-sp-6 px-sp-4">
          {!revokeConfirm ? (() => {
            const keySecret = selectedKeyId
              ? (localStorage.getItem(`imago:key:${address}:${selectedKeyId}`) || storedKey || '')
              : storedKey
            const maskedSuffix = keySecret.length > 0 ? keySecret.slice(-4) : '????'

            return (
              <>
                {/* Grouped card: Key name + API key */}
                <div className="rounded-[16px] overflow-hidden mb-sp-4" style={{ backgroundColor: 'var(--tg-theme-secondary-bg-color)' }}>

                  {/* Key name row */}
                  <div className="px-sp-4 pt-sp-3 pb-sp-3" style={{ borderBottom: '1px solid var(--tg-theme-bg-color)' }}>
                    <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--tg-theme-hint-color)' }}>
                      Key name
                    </p>
                    <div className="flex items-center gap-sp-2">
                      <input
                        ref={nameInputRef}
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                        readOnly={!nameEditing}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); handleSaveName() }
                          if (e.key === 'Escape') setNameEditing(false)
                        }}
                        className="flex-1 bg-transparent outline-none text-[15px]"
                        style={{
                          color: 'var(--tg-theme-text-color)',
                          caretColor: 'var(--tg-theme-accent-text-color)',
                        }}
                        maxLength={40}
                      />
                      <button
                        onClick={() => {
                          if (nameEditing) {
                            handleSaveName()
                          } else {
                            nameOriginalRef.current = nameDraft
                            setNameEditing(true)
                            setTimeout(() => nameInputRef.current?.focus(), 50)
                          }
                          haptics.impact('light')
                        }}
                        className="w-8 h-8 flex items-center justify-center rounded-full active:opacity-70 transition-colors shrink-0"
                        style={{ color: nameEditing ? 'var(--tg-theme-accent-text-color)' : 'var(--tg-theme-hint-color)' }}
                      >
                        <Pencil size={15} />
                      </button>
                    </div>
                  </div>

                  {/* API key row */}
                  <div className="px-sp-4 pt-sp-3 pb-sp-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--tg-theme-hint-color)' }}>
                      API key
                    </p>
                    <div className="flex items-center gap-sp-2">
                      <span className="flex-1 text-[15px] font-mono" style={{ color: 'var(--tg-theme-text-color)' }}>
                        sk-imago-••••{maskedSuffix}
                      </span>
                      <button
                        onClick={() => handleCopy(keySecret, 'options-key')}
                        className="w-8 h-8 flex items-center justify-center active:opacity-70 transition-opacity shrink-0"
                        style={{ color: copiedLabel === 'options-key' ? 'var(--tg-theme-button-color)' : 'var(--tg-theme-accent-text-color)' }}
                      >
                        {copiedLabel === 'options-key' ? <Check size={16} /> : <Copy size={16} />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Save changes — visible in edit mode; disabled (gray) until name actually changes */}
                {nameEditing && (() => {
                  const isDirty = nameDraft.trim() !== nameOriginalRef.current.trim() && nameDraft.trim().length > 0
                  return (
                    <button
                      onClick={isDirty ? handleSaveName : undefined}
                      disabled={!isDirty}
                      className="w-full h-14 rounded-[14px] text-[17px] font-semibold mb-sp-3 transition-colors"
                      style={isDirty
                        ? { backgroundColor: 'var(--tg-theme-button-color)', color: 'var(--tg-theme-button-text-color)' }
                        : { backgroundColor: 'var(--tg-theme-secondary-bg-color)', color: 'var(--tg-theme-hint-color)', cursor: 'default' }
                      }
                    >
                      Save changes
                    </button>
                  )
                })()}

                {/* Delete — text-only destructive, no fill (fill is reserved for confirm step) */}
                {selectedKeyId && selectedKeyId !== 'local' && (
                  canRevoke ? (
                    <button
                      onClick={() => { haptics.impact('heavy'); setRevokeConfirm(true) }}
                      className="w-full h-14 flex items-center justify-center active:opacity-70 transition-opacity"
                      style={{ color: 'var(--tg-theme-destructive-text-color)' }}
                    >
                      <span className="text-[17px]">Delete key</span>
                    </button>
                  ) : (
                    <div className="flex items-center justify-center h-14 opacity-40">
                      <span className="text-[15px]" style={{ color: 'var(--tg-theme-hint-color)' }}>
                        Cannot delete the last key
                      </span>
                    </div>
                  )
                )}
              </>
            )
          })() : (
            <div className="flex flex-col gap-sp-3">
              <p className="text-[15px] text-center" style={{ color: 'var(--tg-theme-text-color)' }}>
                This API key will be permanently deleted. Any apps or integrations using it will stop working immediately.
              </p>
              {revokeError && (
                <p className="text-[13px] text-center" style={{ color: 'var(--tg-theme-destructive-text-color)' }}>
                  {revokeError}
                </p>
              )}
              <button
                onClick={handleRevoke}
                className="w-full h-14 rounded-[14px] text-[17px] font-semibold active:opacity-70 transition-opacity"
                style={{ backgroundColor: 'var(--tg-theme-destructive-text-color)', color: '#ffffff' }}
              >
                Delete
              </button>
              <button
                onClick={() => { haptics.impact('light'); setRevokeConfirm(false) }}
                className="w-full h-14 rounded-[14px] text-[17px] font-semibold active:opacity-70 transition-opacity"
                style={{ backgroundColor: 'var(--tg-theme-secondary-bg-color)', color: 'var(--tg-theme-text-color)' }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </BottomSheet>

      <CreateKeySheet
        open={createKeyOpen}
        onClose={() => setCreateKeyOpen(false)}
      />

    </>
  )
}
